import React, { useMemo, useState } from "react";
import MySkeleton from "components/ui/feedback/Skeleton";
import { useStatsFetch } from "./useStatsApi";
import {
  StatCard,
  SectionCard,
  RefreshButton,
  ErrorMessage,
  SimpleTable,
  Badge,
  formatNumber,
} from "./StatComponents";

const KIND_META = [
  { key: "vehicleRoute", label: "Öğrenci servisi", short: "Öğrenci" },
  { key: "personelService", label: "Personel servisi", short: "Personel" },
  { key: "bundledRoute", label: "Toplu servis", short: "Toplu" },
];

const KIND_LABEL = Object.fromEntries(KIND_META.map((item) => [item.key, item.label]));

const PERIODS = [
  { key: "last1", label: "Son 1 gün" },
  { key: "last7", label: "Son 7 gün" },
  { key: "last30", label: "Son 30 gün" },
];

const emptyPeriods = () => ({ last1: 0, last7: 0, last30: 0 });

const periodCountsFor = (row, kindFilter) => {
  if (!kindFilter) return { last1: row.last1 || 0, last7: row.last7 || 0, last30: row.last30 || 0 };
  return row.byKind?.[kindFilter] || emptyPeriods();
};

const SortHeader = ({ label, sortKey, currentKey, dir, onSort, align = "right" }) => {
  const active = currentKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        padding: "10px",
        textAlign: align,
        borderBottom: "2px solid #ddd",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        color: active ? "#1976d2" : "#333",
      }}
    >
      {label}
      {active ? (dir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );
};

const TravelUsageSection = () => {
  const { data, isLoading, error, load } = useStatsFetch("/admin/statistics/travel-usage");
  const [kindFilter, setKindFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("last30");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedId, setExpandedId] = useState(null);

  const totals = useMemo(() => periodCountsFor(data?.totals || emptyPeriods(), kindFilter), [data, kindFilter]);

  const kindTotals = useMemo(() => {
    const byKind = data?.totals?.byKind || {};
    return KIND_META.map((item) => ({
      key: item.key,
      label: item.label,
      ...(byKind[item.key] || emptyPeriods()),
    })).filter((row) => row.last30 > 0 || row.last7 > 0 || row.last1 > 0);
  }, [data]);

  const rows = useMemo(() => {
    const list = (data?.companies || [])
      .map((company) => ({ ...company, counts: periodCountsFor(company, kindFilter) }))
      .filter((company) => {
        if (kindFilter && company.counts.last30 === 0 && company.counts.last7 === 0 && company.counts.last1 === 0) {
          return false;
        }
        if (!search.trim()) return true;
        return String(company.company || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase());
      });

    const factor = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === "company") {
        return factor * String(a.company).localeCompare(String(b.company), "tr");
      }
      return factor * ((a.counts[sortKey] || 0) - (b.counts[sortKey] || 0));
    });
    return list;
  }, [data, kindFilter, search, sortKey, sortDir]);

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "company" ? "asc" : "desc");
  };

  const kindBreakdownColumns = [
    { key: "label", label: "Servis tipi" },
    ...PERIODS.map((period) => ({
      key: period.key,
      label: period.label,
      align: "right",
      render: (row) => formatNumber(row[period.key]),
    })),
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "15px",
        }}
      >
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          <button onClick={() => setKindFilter("")} style={filterButtonStyle(!kindFilter)}>
            Tümü
          </button>
          {KIND_META.map((item) => (
            <button
              key={item.key}
              title={item.label}
              onClick={() => setKindFilter(item.key)}
              style={filterButtonStyle(kindFilter === item.key)}
            >
              {item.short}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Şirket ara..."
            style={{
              padding: "6px 10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "13px",
              minWidth: "180px",
            }}
          />
          <RefreshButton onClick={() => load()} isLoading={isLoading} />
        </div>
      </div>

      <ErrorMessage message={error} />

      {isLoading && !data ? (
        <MySkeleton lineCount={10} />
      ) : data ? (
        <>
          <p style={{ color: "#666", fontSize: "13px", marginTop: 0, marginBottom: "16px" }}>
            Sürücünün başlattığı travel kayıtları (öğrenci servisi, personel servisi, toplu servis). Başlatılmamış
            (yalnızca kontrol) kayıtlar dahil değil. Pencereler takvim günü değil rolling: son 1 / 7 / 30 gün.
            {kindFilter ? ` Gösterilen tip: ${KIND_LABEL[kindFilter]}.` : ""}
          </p>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <StatCard label="Son 1 gün" value={totals.last1} color="#c62828" />
            <StatCard label="Son 7 gün" value={totals.last7} color="#ef6c00" />
            <StatCard label="Son 30 gün" value={totals.last30} color="#1976d2" />
            <StatCard
              label="Şirket"
              value={rows.length}
              sub={search || kindFilter ? `${formatNumber(data.companyCount)} toplam travel başlatan` : "travel başlatan"}
              color="#546e7a"
            />
          </div>

          {!kindFilter && kindTotals.length > 0 && (
            <SectionCard title="Servis Tipi Toplamı">
              <SimpleTable
                columns={kindBreakdownColumns}
                rows={kindTotals}
                rowKey={(row) => row.key}
                maxHeight="280px"
                emptyText="Bu dönemde başlatılmış travel yok."
              />
            </SectionCard>
          )}

          <SectionCard title={`Şirket Bazında Travel (${formatNumber(rows.length)})`}>
            {rows.length === 0 ? (
              <p style={{ color: "#999", margin: 0 }}>Bu filtrede başlatılmış travel yok.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      <SortHeader
                        label="Şirket"
                        sortKey="company"
                        currentKey={sortKey}
                        dir={sortDir}
                        onSort={onSort}
                        align="left"
                      />
                      {PERIODS.map((period) => (
                        <SortHeader
                          key={period.key}
                          label={period.label}
                          sortKey={period.key}
                          currentKey={sortKey}
                          dir={sortDir}
                          onSort={onSort}
                        />
                      ))}
                      <th style={{ padding: "10px", textAlign: "center", borderBottom: "2px solid #ddd" }}>
                        Detay
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isExpanded = expandedId === row.companyId;
                      const kindRows = KIND_META.map((item) => ({
                        key: item.key,
                        label: item.label,
                        ...(row.byKind?.[item.key] || emptyPeriods()),
                      })).filter((item) => item.last30 > 0 || item.last7 > 0 || item.last1 > 0);

                      return (
                        <React.Fragment key={row.companyId}>
                          <tr style={{ background: isExpanded ? "#e3f2fd" : "transparent" }}>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 500 }}>{row.company}</span>
                                {row.isDemo && (
                                  <Badge color="#fff3e0" textColor="#e65100">
                                    Demo
                                  </Badge>
                                )}
                                {row.isActive === false && (
                                  <Badge color="#eeeeee" textColor="#616161">
                                    Pasif
                                  </Badge>
                                )}
                              </div>
                            </td>
                            {PERIODS.map((period) => (
                              <td
                                key={period.key}
                                style={{ ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                              >
                                {formatNumber(row.counts[period.key])}
                              </td>
                            ))}
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : row.companyId)}
                                style={{
                                  background: isExpanded ? "#1976d2" : "none",
                                  color: isExpanded ? "#fff" : "#333",
                                  border: "1px solid #ccc",
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                }}
                              >
                                {isExpanded ? "Kapat" : "Tipler"}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr style={{ background: "#e3f2fd" }}>
                              <td colSpan={5} style={{ padding: "12px 15px", borderBottom: "1px solid #eee" }}>
                                <div style={{ background: "#fff", padding: "12px", borderRadius: "6px" }}>
                                  <h4 style={{ margin: "0 0 8px 0", fontSize: "14px" }}>
                                    {row.company} — servis tipi kırılımı
                                  </h4>
                                  <SimpleTable
                                    columns={kindBreakdownColumns}
                                    rows={kindRows}
                                    rowKey={(item) => item.key}
                                    emptyText="Bu şirketin servis tipi kırılımı yok."
                                    maxHeight="280px"
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      ) : null}
    </div>
  );
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #eee",
  verticalAlign: "middle",
};

const filterButtonStyle = (active) => ({
  padding: "6px 12px",
  borderRadius: "4px",
  border: active ? "1px solid #1976d2" : "1px solid #ccc",
  background: active ? "#1976d2" : "#fff",
  color: active ? "#fff" : "#333",
  cursor: "pointer",
  fontSize: "13px",
});

export default TravelUsageSection;
